import Range from '@allesauto/core/range';
import Tabs from '@allesauto/core/tabs';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts, SupportedCurrencies } from '@allesauto/utils/consts';
import { getSlug } from '@allesauto/utils/funcs';
import clsx from 'clsx';
import React, { FC, Fragment, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

export interface PriceFilterProps {
  priceRanges: SchemaTypes.PriceRange[];
  range: SchemaTypes.PriceRange;
  onChange?: (range: SchemaTypes.PriceRange) => void;
  darkBg?: boolean;
  extraClass?: string;
}

const PriceFilter: FC<React.PropsWithChildren<PriceFilterProps>> = ({
  priceRanges,
  range,
  onChange,
  darkBg,
  extraClass,
}) => {
  const currency = useSelector((state: any) => state.app.currency);
  const intl = useIntl();

  const merchantOrigin = useWhiteLabel()?.merchantOrigin;
  const trackingIdentifier = 'aa-filter';
  const trackingIdentifierPrice = `${trackingIdentifier}--price`;

  function checkWlOrigin(): boolean {
    return merchantOrigin === 'aurego-de';
  }

  const className = {
    title: 'text-white',
    content: 'mt-20px',
    range: 'mt-10px',
    iconCurrencyRange: clsx(
      'absolute bottom-[15px] right-20px',
      darkBg
        ? checkWlOrigin()
          ? 'text-purple80 monochrome:text-black z-10 font-weight-bold'
          : 'text-green60 monochrome:text-white'
        : 'text-purple80 monochrome:text-black'
    ),
  };

  const tabs = priceRanges.map((pr) => {
    if (pr.key === 'CASH_RANGE') {
      return translate(intl, 'ui.PCP.FILTERS.PRICE.CASH_TAB');
    } else if (pr.key === 'LOAN_RATE_RANGE') {
      return translate(intl, 'ui.PCP.FILTERS.PRICE.FINANCE_TAB');
    } else if (pr.key === 'LEASING_RATE_RANGE') {
      return translate(intl, 'ui.PCP.FILTERS.PRICE.LEASING_TAB');
    } else {
      return pr.key || '';
    }
  });

  const onTabChange = useCallback(
    (tabIndex: number) => {
      if (onChange) {
        onChange({ ...priceRanges[tabIndex] });
      }
    },
    [onChange, priceRanges]
  );

  const onRangeChange = useCallback(
    (value: SchemaTypes.PriceRange) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange]
  );

  const tabsSelectedIndex = useMemo(
    () => priceRanges.findIndex((pr) => pr.key === range.key),
    [priceRanges, range]
  );

  return (
    <Tabs
      tabs={tabs}
      selectedIndex={tabsSelectedIndex}
      onChange={onTabChange}
      contentWrapperClassName={className.content}
      fullWidth
      isDarkMode={darkBg}
      isPcpPage={true}
    >
      {tabs.map((tab, index) => (
        <Fragment key={tab}>
          {priceRanges[index].key && (
            <label className={className.title}>
              {translate(
                intl,
                `ui.PCP.FILTERS.PRICE.RANGE_TITLE_TAB.${priceRanges[
                  index
                ].key.toUpperCase()}`
              )}
            </label>
          )}
          <Range
            id={`${trackingIdentifierPrice}-${getSlug(tab)}`}
            min={priceRanges[index].min ?? 0}
            max={priceRanges[index].max ?? 0}
            valueMin={
              (range.key === priceRanges[index].key
                ? range.min
                : priceRanges[index].min) ?? 0
            }
            valueMax={
              (range.key === priceRanges[index].key
                ? range.max
                : priceRanges[index].max) ?? 0
            }
            onChange={({ min, max }) =>
              onRangeChange({
                key: priceRanges[index].key,
                min,
                max,
              })
            }
            rangeLabelText={translate(intl, 'ui.PCP.FILTERS.PRICE.RANGE_LABEL')}
            icon={
              <span className={className.iconCurrencyRange}>
                <label>
                  {consts.CURRENCIES[currency as SupportedCurrencies].SHORT}
                </label>
              </span>
            }
            className={className.range}
            darkBg={darkBg}
            extraClass={extraClass}
          />
        </Fragment>
      ))}
    </Tabs>
  );
};

export default PriceFilter;
